import { Task, Update, child, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import type { FolderId, JSON, Contract, ContractField, Signer } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { currentLocalISODate, SdkClient, type SdkFailure } from '~/effects'
import {
  identityVerificationModule,
  initialIdentityVerification,
  type IdentityVerificationState,
  type IdentityVerificationMessage
} from './identity-verification-module'

const verification = child(identityVerificationModule, 'verification')

/**
 * Client-side contract signing: the linear wizard a taxpayer (and spouse)
 * walk through to sign an engagement letter or e-file authorization packet.
 *
 * This module owns the PDF-blind signing wizard: which screen is up
 * (`ContractSigningState`), the values being edited, and the save/submit
 * Commands, and the nested identity-verification journey. The shell adapts
 * this journey to the PDF viewer and surface visibility.
 *
 * The data model is deliberately flat: the contract's `signers` list is the
 * authority on who signs, in what order, and under what display name; each
 * field references one signer by its contract-scoped ID.
 * Per-signer progress is derived on demand from the field list — there is
 * no separate progress state to keep consistent. A field whose signer and
 * the signer list disagree is a provider bug and crashes at the point
 * of use rather than silently missing from (or idling in) the wizard.
 */

export type ContractKind = 'tax_return_delivery' | 'engagement_letter'

export function contractKind(kind: Contract['kind']): ContractKind {
  return match(kind)
    .returnType<ContractKind>()
    .with(P.union('tax_return_delivery', 'engagement_letter'), (kind) => kind)
    .otherwise((kind) => {
      throw new Error(`Unknown signature contract provider: ${kind}`)
    })
}

/**
 * The saved answers and unsaved edits shown in the signing form.
 *
 * `data` contains loaded values and values accepted for background saving; `changes` holds only paths
 * whose value differs from `data` — an edit back to the saved value removes
 * its entry again (`putChange`). A save owns exactly `changes`; accepting it
 * folds them into `data` so later signers cannot resend an older snapshot.
 * `pendingSaves` counts those accepted writes. Submission and session teardown
 * wait for their acknowledgements; moving to the next signer does not.
 */
export type ContractChangeset = {
  data: Record<string, string>
  changes: Record<string, string>
}

type SigningSessionBase = {
  folderId: FolderId
  contractId: string
  kind: ContractKind
  fields: ContractField[]
  signers: Signer[]
  verificationPolicy: Contract['identityVerification']
  attemptedSignerIds: string[]
  signatureNames: Record<string, string>
  changeset: ContractChangeset
  pendingSaves: number
}

type SigningSession = SigningSessionBase & {
  localDate: string
}

export type SigningTarget = { type: 'signer'; signerId: string } | { type: 'finish' }

// Read the browser's local date once; all automatic dates in this session use it.
type OpeningState = SigningSessionBase & {
  status: 'opening'
  target: SigningTarget
}

// An absent activeFieldId means this signer has not started. Capture can edit
// an earlier signature without changing the field reached by guided signing.
type SigningState = SigningSession & {
  status: 'signing'
  signerId: string
} & (
    | { activeFieldId: undefined; captureFieldId: undefined }
    | { activeFieldId: string; captureFieldId: undefined }
    | { activeFieldId: string; captureFieldId: string }
  )

// The previous signer's work is accepted. Continue selects the next pending signer
// and their first unfinished field together; Finish later closes the dialog.
type HandoffState = SigningSession & {
  status: 'handoff'
}

// Signature capture is finished; the identity-verification child owns the
// questions and answers. Its completion is settled here before moving on.
type VerifyingIdentityState = SigningSession & {
  status: 'verifyingIdentity'
  signerId: string
  verification: IdentityVerificationState
}

type DoneState = {
  // Terminal result, consumed immediately by the shell; not a hidden session.
  status: 'done'
}

// Terminal result: the folder refused submission because a required invoice is
// unpaid. Submission starts only after every save is acknowledged, so the
// signatures stay saved; the shell explains the payment requirement.
type PaymentRequiredState = {
  /** Terminal signing outcome after the server refuses submission for an unpaid invoice. */
  status: 'paymentRequired'
}

type SubmittingState = SigningSession & {
  status: 'submitting'
}

type FinishingState = SigningSession & {
  status: 'finishing'
  // Leaving ends the interaction, but an accepted write finishes before a new
  // signing session starts. The Command owns its input; this state only waits
  // for its completion. Reopening afterward uses the persisted contract.
  pending: 'saving' | 'submitting'
}

/**
 * opening → signing; Start assigns the signer's first unfinished field
 * handoff --Continue as signer--> signing
 * signing → verifyingIdentity | handoff | submitting (waiting for background saves)
 *
 * Completion is queried from accepted answers. Close also saves partial work; navigation hides an accepted write
 * in `finishing` until success arrives. Closing verification discards its input.
 * Unhandled execution failures exit through the application host, not this flow.
 */
export type ContractSigningState =
  | DoneState
  | PaymentRequiredState
  | OpeningState
  | SigningState
  | HandoffState
  | VerifyingIdentityState
  | SubmittingState
  | FinishingState

/** Any state in which the signing experience is visible. */
export type ActiveContractSigningState = Exclude<
  ContractSigningState,
  { status: 'done' | 'paymentRequired' | 'finishing' }
>

export function activeContractSigningState(
  state: ContractSigningState
): ActiveContractSigningState | undefined {
  if (state.status == 'done' || state.status == 'paymentRequired' || state.status == 'finishing')
    return undefined
  return state
}

export function displayedContractSignerId(state: ActiveContractSigningState): string | undefined {
  return match(state)
    .returnType<string | undefined>()
    .with({ status: 'opening' }, ({ target }) =>
      match(target)
        .with({ type: 'signer' }, ({ signerId }) => signerId)
        .with({ type: 'finish' }, () => undefined)
        .exhaustive()
    )
    .with({ status: P.union('signing', 'verifyingIdentity') }, ({ signerId }) => signerId)
    .with({ status: P.union('handoff', 'submitting') }, () => undefined)
    .exhaustive()
}

export type SigningDialogModel = {
  /** Who is signing right now; absent between signers and after all have signed. */
  signerName: string | undefined
  /**
   * What dismissing the dialog (Escape or the header Close) means right now.
   * Capture is a nested surface, so dismissal cancels it rather than the
   * session. An accepted write in flight (submission, verification start or
   * submit) cannot be dismissed at all until it settles.
   */
  dismiss: ContractSigningMessage | undefined
  /** The header's one primary control; absent while an overlay owns the next step. */
  action:
    | { type: 'act'; label: string; message: ContractSigningMessage; enabled: boolean }
    | { type: 'waiting'; label: string }
    | undefined
  /** The guided field that carries the "Sign here" flag. */
  signHereField: ContractField | undefined
  overlay:
    | { type: 'handoff'; nextSignerName: string; enabled: boolean }
    | { type: 'verification'; verification: IdentityVerificationState; signaturesSaved: boolean }
    | {
        type: 'capture'
        title: string
        fieldLabel: string
        inputLabel: string
        initialValue: string
        enabled: boolean
      }
    | undefined
}

/**
 * Everything the signing dialog shows for the current phase. An archived
 * folder shows the document but disables every write; busy phases disable
 * dismissal.
 */
export function selSigningDialog(
  state: ActiveContractSigningState,
  isArchived: boolean
): SigningDialogModel {
  const signerId = displayedContractSignerId(state)
  const act = (label: string, message: ContractSigningMessage): SigningDialogModel['action'] => ({
    type: 'act',
    label,
    message,
    enabled: !isArchived
  })
  return {
    signerName: match(signerId)
      .with(undefined, () => undefined)
      .otherwise((signerId) => signerLabel(state.signers, signerId)),
    dismiss: match(state)
      .returnType<ContractSigningMessage | undefined>()
      .with({ status: 'submitting' }, () => undefined)
      .with(
        {
          status: 'verifyingIdentity',
          verification: { status: P.union('starting', 'submitting') }
        },
        () => undefined
      )
      .with({ status: 'signing', captureFieldId: P.string }, () => ({
        type: 'signatureCaptureClosed'
      }))
      .otherwise(() => ({ type: 'closed' })),
    action: match(state)
      .returnType<SigningDialogModel['action']>()
      .with({ status: 'opening' }, () => ({ type: 'waiting', label: 'Loading…' }))
      .with({ status: 'signing', activeFieldId: undefined }, () =>
        act('Start', { type: 'startClicked' })
      )
      .with({ status: 'signing' }, () => act('Sign', { type: 'signatureCaptureOpened' }))
      .with({ status: 'submitting' }, () => ({ type: 'waiting', label: 'Finishing…' }))
      // These phases render their own overlay with its own buttons.
      .with({ status: P.union('handoff', 'verifyingIdentity') }, () => undefined)
      .exhaustive(),
    signHereField: match(state)
      .with({ status: 'signing' }, activeSignatureField)
      .otherwise(() => undefined),
    overlay: match(state)
      .returnType<SigningDialogModel['overlay']>()
      .with({ status: 'handoff' }, (state) => ({
        type: 'handoff',
        nextSignerName: signerLabel(state.signers, nextPendingSignerId(state)),
        enabled: !isArchived
      }))
      .with({ status: 'verifyingIdentity' }, ({ verification, pendingSaves }) => ({
        type: 'verification',
        verification,
        signaturesSaved: pendingSaves === 0
      }))
      .with({ status: 'signing', captureFieldId: P.string }, (state) => {
        const field = signatureCaptureField(state)
        return {
          type: 'capture',
          title: `Sign as ${signerLabel(state.signers, field.signerId)}`,
          fieldLabel: signingFieldLabel(state.signers, field),
          inputLabel: field.label,
          initialValue: signatureName(state, field.signerId),
          enabled: !isArchived
        }
      })
      .otherwise(() => undefined)
  }
}

/** Guided signing permits the current field and edits to earlier answers. */
export function signatureEditable(state: ActiveContractSigningState, field: ContractField) {
  if (state.status != 'signing' || state.activeFieldId == undefined) return false
  return (
    field.type == 'signature' &&
    field.signerId == state.signerId &&
    (field.id == state.activeFieldId || Boolean(fieldValues(state.changeset)[field.id]?.trim()))
  )
}

type ContractSigningOpened = {
  type: 'opened'
  folderId: FolderId
  contract: Contract & { status: 'published' }
  target: SigningTarget
}

export type ContractSigningMessage =
  | ContractSigningOpened
  | { type: 'closed' }
  | { type: 'continueAsNextSigner' }
  | { type: 'finishLaterClicked' }
  | { type: 'valuesSaved' }
  | { type: 'submissionSucceeded' }
  | { type: 'paymentRequired' }
  | { type: 'signatureCaptured'; value: string }
  | { type: 'signatureSelected'; fieldId: string }
  | { type: 'startClicked' }
  | { type: 'signatureCaptureOpened' }
  | { type: 'signatureCaptureClosed' }

export type ContractSigningWizardMessage =
  | ContractSigningMessage
  | { type: 'verification'; message: IdentityVerificationMessage }
  | TaskMessage<typeof prepareLocalDate>
  // Context loss by navigation is an implicit Close and saves captured
  // values; a session end (logout) cannot save — the credentials that the
  // save command needs are already gone — so it discards.
  | { type: 'contextLost'; reason: 'navigation' | 'sessionEnded' }

const prepareLocalDate = Task.define(() =>
  Effect.map(
    currentLocalISODate,
    (localDate) => ({ type: 'localDatePrepared', localDate }) as const
  )
)

export const contractSigningWizardModule = defModule<{ state: ContractSigningState; message: ContractSigningWizardMessage; init: ContractSigningOpened }>({
  init: startOpeningSigningWizard,
  tasks: (state) =>
    match(state)
      .with({ status: 'opening' }, () => [prepareLocalDate()])
      .with({ status: 'verifyingIdentity' }, (state) => verification.tasks(state.verification))
      .otherwise(() => []),
  update: updateContractSigningWizard
})

function updateContractSigningWizard(
  state: ContractSigningState,
  message: ContractSigningWizardMessage
): UpdateResult<ContractSigningState, ContractSigningWizardMessage> {
  return (
    match({ state, message })
      .returnType<UpdateResult<ContractSigningState, ContractSigningWizardMessage>>()
      // Close and navigation hide immediately and persist captured edits. Logout discards
      // unfinished input and cannot start a save. Accepted writes keep ownership.
      .with(
        {
          state: { status: 'opening' },
          message: { type: 'closed' }
        },
        () => ({ status: 'done' })
      )
      .with(
        P.union(
          {
            state: { status: P.union('signing', 'handoff') },
            message: { type: 'closed' }
          },
          { state: { status: 'handoff' }, message: { type: 'finishLaterClicked' } }
        ),
        ({ state }) => {
          return closeSession(state)
        }
      )
      .with({ state: { status: 'verifyingIdentity' }, message: { type: 'closed' } }, ({ state }) =>
        closeSession(state)
      )
      .with({ state: { status: 'submitting' }, message: { type: 'contextLost' } }, ({ state }) => ({
        ...state,
        status: 'finishing',
        pending: 'submitting'
      }))
      .with(
        { state: { status: 'finishing' }, message: { type: 'contextLost' } },
        ({ state }) => state
      )
      .with(
        {
          state: { status: P.union('signing', 'handoff') },
          message: { type: 'contextLost', reason: 'navigation' }
        },
        ({ state }) => {
          return closeSession(state)
        }
      )
      .with(
        {
          state: { status: 'verifyingIdentity' },
          message: { type: 'contextLost', reason: 'navigation' }
        },
        ({ state }) => closeSession(state)
      )
      .with({ state: { status: 'opening' }, message: { type: 'contextLost' } }, () => ({
        status: 'done'
      }))
      .with(
        {
          state: {
            status: P.union('signing', 'handoff', 'verifyingIdentity')
          },
          message: { type: 'contextLost', reason: 'sessionEnded' }
        },
        ({ state }) => {
          if (state.pendingSaves == 0) return { status: 'done' }
          return { ...sessionFrom(state), status: 'finishing', pending: 'saving' }
        }
      )
      // Main journey: open → sign → verify → handoff → submit.
      .with(
        { state: { status: 'opening' }, message: { type: 'localDatePrepared' } },
        ({ state, message }) => {
          const { status: _status, target, ...session } = state
          return initialPhase({ ...session, localDate: message.localDate }, target)
        }
      )
      // Guided signing: start, open/cancel capture, capture, persist.
      .with(
        {
          state: { status: 'signing', activeFieldId: undefined },
          message: { type: 'startClicked' }
        },
        ({ state }) => {
          const [field] = remainingSignatureFields(state.fields, state.changeset, state.signerId)
          if (!field) return completeSigner(state, state.signerId)
          return { ...state, activeFieldId: field.id, captureFieldId: undefined }
        }
      )
      .with(
        {
          state: { status: 'signing', activeFieldId: P.string, captureFieldId: undefined },
          message: { type: 'signatureSelected' }
        },
        ({ state, message }) => {
          const field = state.fields.find((field) => field.id == message.fieldId)
          if (!field || !signatureEditable(state, field))
            throw new Error('Signature is not editable in the current signing state')
          return { ...state, captureFieldId: field.id }
        }
      )

      .with(
        {
          state: { status: 'signing', activeFieldId: P.string, captureFieldId: undefined },
          message: { type: 'signatureCaptureOpened' }
        },
        ({ state }) => ({
          ...state,
          captureFieldId: state.activeFieldId
        })
      )
      .with(
        {
          state: { status: 'signing', captureFieldId: P.string },
          message: { type: 'signatureCaptureClosed' }
        },
        ({ state: { captureFieldId: _captureFieldId, ...state } }) => ({
          ...state,
          captureFieldId: undefined
        })
      )
      .with(
        {
          state: { status: 'signing', captureFieldId: P.string },
          message: { type: 'signatureCaptured' }
        },
        ({ state, message }) => {
          const session = captureSignature(state, message.value)
          const [nextField] = remainingSignatureFields(
            session.fields,
            session.changeset,
            state.signerId
          )
          if (nextField)
            return {
              ...session,
              status: 'signing',
              signerId: state.signerId,
              activeFieldId: nextField.id,
              captureFieldId: undefined
            }
          // Accept the signer's completed values; persistence does not delay handoff.
          return completeSigner(session, state.signerId)
        }
      )
      .with(
        {
          state: {
            status: P.union('signing', 'handoff', 'verifyingIdentity'),
            pendingSaves: P.number.gt(0)
          },
          message: { type: 'valuesSaved' }
        },
        ({ state }) => {
          return { ...state, pendingSaves: state.pendingSaves - 1 }
        }
      )
      // Leaving this phase discards the quiz and revokes its Task delivery.
      .with(
        { state: { status: 'verifyingIdentity' }, message: { type: 'verification' } },
        ({ state, message }) =>
          Update.pipe(
            verification.update(state.verification, message.message, (verification) =>
              settleVerification({ ...state, verification })
            ),
            submitWhenSaved
          )
      )
      // Handoff starts the next signer.
      .with(
        { state: { status: 'handoff' }, message: { type: 'continueAsNextSigner' } },
        ({ state, message }) => {
          if (hasUnsavedChanges(state.changeset)) throw unexpectedMessage(state, message)
          const signerId = nextPendingSignerId(state)
          const [field] = remainingSignatureFields(state.fields, state.changeset, signerId)
          if (!field) return completeSigner(state, signerId)
          return {
            ...sessionFrom(state),
            status: 'signing',
            signerId,
            activeFieldId: field.id,
            captureFieldId: undefined
          }
        }
      )
      .with({ state: { status: 'submitting' }, message: { type: 'submissionSucceeded' } }, () => ({
        status: 'done'
      }))
      // A context loss hides the flow but does not revoke an accepted Command.
      .with(
        {
          state: { status: 'finishing', pending: 'saving', pendingSaves: P.number.gt(0) },
          message: { type: 'valuesSaved' }
        },
        ({ state }) => {
          if (state.pendingSaves == 1) return { status: 'done' }
          return { ...state, pendingSaves: state.pendingSaves - 1 }
        }
      )
      .with(
        P.union(
          {
            state: { status: 'submitting', pendingSaves: P.number.gt(0) },
            message: { type: 'valuesSaved' }
          },
          {
            state: { status: 'finishing', pending: 'submitting', pendingSaves: P.number.gt(0) },
            message: { type: 'valuesSaved' }
          }
        ),
        ({ state }) => {
          const next = { ...state, pendingSaves: state.pendingSaves - 1 }
          if (next.pendingSaves > 0) return next
          return [next, submitContract(state)]
        }
      )
      .with(
        {
          state: { status: 'finishing', pending: 'submitting' },
          message: { type: 'submissionSucceeded' }
        },
        () => ({ status: 'done' })
      )
      .with(
        {
          state: { status: P.union('submitting', 'finishing') },
          message: { type: 'paymentRequired' }
        },
        () => ({ status: 'paymentRequired' })
      )
      .otherwise(() => {
        throw unexpectedMessage(state, message)
      })
  )
}

export function startOpeningSigningWizard(
  message: Extract<ContractSigningWizardMessage, { type: 'opened' }>
): ActiveContractSigningState {
  if (!message.contract.id) throw new Error('Cannot sign an unpublished contract')
  const fields = message.contract.fields
  const changeset = changesetFromContract(message.contract, fields)

  const state: OpeningState = {
    status: 'opening',
    target: message.target,
    folderId: message.folderId,
    contractId: message.contract.id,
    kind: contractKind(message.contract.kind),
    fields,
    signers: message.contract.signers,
    verificationPolicy: message.contract.identityVerification,
    attemptedSignerIds: attemptedSignerIds(message.contract),
    signatureNames: {},
    pendingSaves: 0,
    changeset
  }
  return state
}

function captureSignature(
  state: SigningState & { captureFieldId: string },
  value: string
): SigningSession {
  const field = signatureCaptureField(state)

  let changeset = putChange(state.changeset, field.id, value)
  const dates = state.fields.filter(
    (candidate) =>
      candidate.type == 'date' &&
      candidate.signerId == field.signerId &&
      candidate.page == field.page
  )
  if (dates.length == 1) {
    changeset = fillEmptyDates(changeset, dates, state.localDate)
  }

  return {
    ...sessionFrom(state),
    changeset,
    signatureNames: { ...state.signatureNames, [state.signerId]: value }
  }
}

function initialPhase(
  session: SigningSession,
  target: SigningTarget
): UpdateResult<ContractSigningState, ContractSigningWizardMessage> {
  if (target.type == 'finish') {
    if (pendingSignerIds(session).length != 0)
      throw new Error('Cannot finish while a signer has remaining work')
    return Update.pipe({ ...sessionFrom(session), status: 'submitting' }, submitWhenSaved)
  }
  const { signerId } = target
  contractSigner(session.signers, signerId)
  const [field] = remainingSignatureFields(session.fields, session.changeset, signerId)
  if (!field) return completeSigner(session, signerId)
  return {
    ...sessionFrom(session),
    status: 'signing',
    signerId,
    activeFieldId: undefined,
    captureFieldId: undefined
  }
}

// One business decision after accepting the signer's values, including when there were no edits.
function afterSignerCompleted(session: SigningSession, signerId: string): ContractSigningState {
  if (signerNeedsVerification(session, signerId)) {
    return {
      ...sessionFrom(session),
      status: 'verifyingIdentity',
      signerId,
      verification: initialIdentityVerification({
        folderId: session.folderId,
        contractId: session.contractId,
        signerId,
        name: signatureName(session, signerId)
      })
    }
  }
  if (pendingSignerIds(session).length > 0) return { ...sessionFrom(session), status: 'handoff' }
  return { ...sessionFrom(session), status: 'submitting' }
}

function settleVerification(state: VerifyingIdentityState): ContractSigningState {
  if (state.verification.status != 'completed') return state
  const session = {
    ...sessionFrom(state),
    attemptedSignerIds: [...state.attemptedSignerIds, state.signerId]
  }
  return afterSignerCompleted(session, state.signerId)
}

/** Saves from different signers own disjoint fields. Acknowledgements never replace newer local values. */
function completeSigner(
  session: SigningSession,
  signerId: string
): UpdateResult<ContractSigningState, ContractSigningWizardMessage> {
  const completed = {
    ...sessionFrom(session),
    changeset: fillEmptyDates(
      session.changeset,
      session.fields.filter((field) => field.type == 'date' && field.signerId == signerId),
      session.localDate
    )
  }
  if (!hasUnsavedChanges(completed.changeset)) {
    return Update.pipe(afterSignerCompleted(completed, signerId), submitWhenSaved)
  }
  const saving: SigningSession = {
    ...completed,
    changeset: { data: fieldValues(completed.changeset), changes: {} },
    pendingSaves: completed.pendingSaves + 1
  }
  return [afterSignerCompleted(saving, signerId), saveValues(completed)]
}

// Final confirmation accepts submission. Only start it after every signer save is acknowledged.
const submitWhenSaved = Update.effect<ContractSigningState, ContractSigningWizardMessage>(
  (state) => {
    if (state.status == 'submitting' && state.pendingSaves == 0) return submitContract(state)
  }
)

function closeSession(
  state: SigningSession
): UpdateResult<ContractSigningState, ContractSigningWizardMessage> {
  const finishing: FinishingState = {
    ...sessionFrom(state),
    status: 'finishing',
    pending: 'saving'
  }
  if (hasUnsavedChanges(state.changeset)) {
    return [{ ...finishing, pendingSaves: state.pendingSaves + 1 }, saveValues(state)]
  }
  if (state.pendingSaves > 0) return finishing
  return { status: 'done' }
}

function sessionFrom(session: SigningSession): SigningSession {
  return {
    folderId: session.folderId,
    contractId: session.contractId,
    kind: session.kind,
    fields: session.fields,
    signers: session.signers,
    verificationPolicy: session.verificationPolicy,
    attemptedSignerIds: session.attemptedSignerIds,
    signatureNames: session.signatureNames,
    changeset: session.changeset,
    pendingSaves: session.pendingSaves,
    localDate: session.localDate
  }
}

// Operational failures stay in Effect's error channel. Only successful writes
// re-enter the wizard; the application host owns unhandled execution failure.
function saveValues(state: SigningSession) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    yield* sdk.send({
      type: 'folders/fillContractFormFields',
      folderId: state.folderId,
      contractId: state.contractId,
      values: serializeChanges(state)
    })
    return { type: 'valuesSaved' } as const
    // No failure has a product response yet, so an unexpected one is a defect.
  }).pipe(Effect.orDie)
}

function submitContract(state: SigningSession) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    yield* sdk.send({
      type: 'folders/submitContract',
      folderId: state.folderId,
      contractId: state.contractId
    })
    return { type: 'submissionSucceeded' } as const
    // Beyond the unpaid invoice, an unexpected failure is a defect.
  }).pipe(recoverPaymentRequired, Effect.orDie)
}

// The one expected refusal: a required invoice is unpaid when the client submits.
function recoverPaymentRequired<A, R>(effect: Effect.Effect<A, SdkFailure, R>) {
  return effect.pipe(
    Effect.catchTag('HttpStatusError', (error) =>
      match(error.response)
        .with({ status: 409, body: { error: 'payment_required' } }, () =>
          Effect.succeed({ type: 'paymentRequired' } as const)
        )
        .otherwise(() => Effect.fail(error))
    )
  )
}

function unexpectedMessage(state: ContractSigningState, message: { type: string }) {
  return new Error(`Unexpected ${message.type} while signature contract is ${state.status}`)
}

// Contract field and changeset algebra
export function hasUnsavedChanges(changeset: ContractChangeset) {
  return Object.keys(changeset.changes).length > 0
}

export function fieldValues(changeset: ContractChangeset) {
  return { ...changeset.data, ...changeset.changes }
}

export function changesetFromContract(
  contract: Contract,
  fields: ContractField[]
): ContractChangeset {
  return {
    data: Object.fromEntries(
      fields.map((field) => [field.id, fieldValueText(field, contract.form.data[field.id])])
    ),
    changes: {}
  }
}

function attemptedSignerIds(contract: Contract): string[] {
  return contract.activity.flatMap((activity) =>
    match(activity)
      .with({ type: 'identity_verification' }, ({ signerId }) => [signerId])
      .otherwise(() => [])
  )
}

export function signerComplete(
  fields: ContractField[],
  changeset: ContractChangeset,
  signerId: string
) {
  return remainingSignatureFields(fields, changeset, signerId).length == 0
}

/** Signer role keys in signing order — the contract's signer list is the authority. */
export function signerIds(signers: Signer[]) {
  return signers.map((signer) => signer.id)
}

export function contractSigner(signers: Signer[], signerId: string) {
  const signer = signers.find((candidate) => candidate.id == signerId)
  if (!signer) throw new Error(`Unknown contract signer: ${signerId}`)
  return signer
}

/** Display name from the contract's signer list, e.g. "Sam Rivera". */
export function signerLabel(signers: Signer[], signerId: string) {
  // Publication requires every signer's name, and signing starts after publication.
  return contractSigner(signers, signerId).displayName!
}

/** Title-cased signer role, e.g. "Taxpayer". */
export function signerRoleTitle(role: string) {
  return role.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function signingFieldLabel(signers: Signer[], field: ContractField, includeSigner = false) {
  let label = field.label
  if (includeSigner) label = `${signerLabel(signers, field.signerId)} ${label}`
  const formCode = fieldFormCode(field)
  if (formCode) label = `${formCode} ${label}`
  return label
}

function fieldFormCode(field: ContractField) {
  const formCode = field.metadata.form_code
  if (typeof formCode == 'string') return formCode
  return undefined
}

function editableContractFields(fields: ContractField[], signerId: string) {
  return fields.filter((field) => field.signerId == signerId && field.type == 'signature')
}

type SignerProgress = Pick<
  SigningSession,
  'fields' | 'changeset' | 'verificationPolicy' | 'attemptedSignerIds'
>

/** The card and wizard use the same definition of a signer's remaining work. */
export function contractSignerEntries(
  contract: Contract
): Array<{ signer: Signer; pending: boolean }> {
  const progress: SignerProgress = {
    fields: contract.fields,
    changeset: changesetFromContract(contract, contract.fields),
    verificationPolicy: contract.identityVerification,
    attemptedSignerIds: attemptedSignerIds(contract)
  }
  return contract.signers.map((signer) => ({ signer, pending: signerPending(progress, signer.id) }))
}

function signerNeedsVerification(session: SignerProgress, signerId: string) {
  // A recorded attempt avoids forcing another on reopen. Failed remains failed.
  return session.verificationPolicy?.type == 'kba' && !session.attemptedSignerIds.includes(signerId)
}

export function signerPending(session: SignerProgress, signerId: string) {
  return (
    !signerComplete(session.fields, session.changeset, signerId) ||
    signerNeedsVerification(session, signerId)
  )
}

function pendingSignerIds(session: SigningSession) {
  return signerIds(session.signers).filter((signerId) => signerPending(session, signerId))
}

export function nextPendingSignerId(state: HandoffState): string {
  const [signerId] = pendingSignerIds(state)
  if (!signerId) throw new Error('Handoff requires a pending signer')
  return signerId
}

export function activeSignatureField(state: SigningState): ContractField | undefined {
  if (state.activeFieldId == undefined) return undefined
  const field = state.fields.find((field) => field.id == state.activeFieldId)
  if (!field) throw new Error(`Unknown active signature: ${state.activeFieldId}`)
  return field
}

export function signatureCaptureField(state: SigningState & { captureFieldId: string }) {
  const field = state.fields.find((field) => field.id == state.captureFieldId)
  if (!field) throw new Error(`Unknown captured signature: ${state.captureFieldId}`)
  return field
}

/** Reuse only this signer's signature, never another person's name. */
export function signatureName(
  session: Pick<SigningSession, 'fields' | 'changeset' | 'signers' | 'signatureNames'>,
  signerId: string
): string {
  const remembered = session.signatureNames[signerId]
  if (remembered != undefined) return remembered
  const values = fieldValues(session.changeset)
  const signed = [...session.fields]
    .sort((a, b) => b.page - a.page || b.rect.y - a.rect.y)
    .find(
      (field) => field.type == 'signature' && field.signerId == signerId && values[field.id]?.trim()
    )
  if (signed) return values[signed.id]
  return signerLabel(session.signers, signerId)
}

export function remainingSignatureFields(
  fields: ContractField[],
  changeset: ContractChangeset,
  signerId: string
) {
  const values = fieldValues(changeset)
  return editableContractFields(fields, signerId)
    .filter((field) => !values[field.id]?.trim())
    .sort((a, b) => a.page - b.page || a.rect.y - b.rect.y)
}

// Both signing transitions preserve dates already entered or saved.
function fillEmptyDates(changeset: ContractChangeset, fields: ContractField[], localDate: string) {
  const values = fieldValues(changeset)
  return fields.reduce((result, field) => {
    if (values[field.id]?.trim()) return result
    return putChange(result, field.id, localDate)
  }, changeset)
}

function putChange(changeset: ContractChangeset, fieldId: string, value: string) {
  const changes = { ...changeset.changes }
  if (changeset.data[fieldId] == value) {
    delete changes[fieldId]
  } else {
    changes[fieldId] = value
  }
  return { ...changeset, changes }
}

function serializeChanges(state: SigningSession) {
  return Object.fromEntries(
    Object.entries(state.changeset.changes).map(([fieldId, value]) => {
      const field = state.fields.find((candidate) => candidate.id == fieldId)
      if (!field) throw new Error(`Unknown signature contract field change: ${fieldId}`)
      return [fieldId, fieldValueJSON(field, value)]
    })
  )
}

function fieldValueJSON(field: ContractField, value: string): JSON {
  const text = value.trim()
  if (!text) return null
  if (field.type == 'signature') return { kind: 'typed', text, font: 'Allura' }
  return text
}

function fieldValueText(field: ContractField, value: JSON | undefined): string {
  if (value == null) return ''
  if (field.type == 'signature') return (value as { text: string }).text
  return value as string
}
